import { useState, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { Avatar, List } from 'antd';
import './styles.less';
import { getSignatureColor } from 'components/editor/shared/components/SignatureBaseButton';
import Icon from 'components/icon';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import { ContentType } from 'utils/getContentType';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useGetCrmContactByIdQuery } from 'hooks/useGetCrmContactByIdQuery';
import { useCrmContactsByCompanyId } from 'pages/flex-data/flex-data-links/useCrmContactsByCompanyId';
import { useAccountUsersQuery } from 'hooks/useAccountUsersQuery';
import { useSigneeOptionsQuery } from './useSigneeOptionsQuery';
import { buildSigneeOptions } from './buildSigneeOptions';
import type { SignatureSelectOption } from './types';

export type { SignatureSelectOption } from './types';

import { toProspectCompositeId } from 'services/company-and-contacts/mappers';
import { ProspectType } from 'services/repositories/interfaces/ProspectRepository';

type Props = {
  value: SignatureSelectOption | null | undefined;
  onChange: (newValue: SignatureSelectOption | null | undefined) => void;
  accountId: number;
  placeholder?: string;
  label?: string;
};

export function SignatureSelect({ value, onChange, accountId, placeholder, label }: Props) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const contentId = useAppSelector(selectContentId);
  const { data: documentData } = useContentMetadata(ContentType.DOCUMENT, contentId);
  const prospectId = toProspectCompositeId(accountId, documentData?.prospect);
  const isCrmPersonProspect = !!prospectId && documentData?.prospect?.type === ProspectType.PERSON;
  const isCrmCompanyProspect = !!prospectId && !isCrmPersonProspect;
  const {
    contacts: crmContacts,
    isLoading: isCrmContactsLoading,
    refetch: refetchCrmContacts,
  } = useCrmContactsByCompanyId({
    companyId: isCrmCompanyProspect ? prospectId : undefined,
    enabled: open && isCrmCompanyProspect,
  });
  const {
    data: crmContact,
    isFetching: isCrmContactFetching,
    refetch: refetchCrmContact,
  } = useGetCrmContactByIdQuery(isCrmPersonProspect ? prospectId : null, {
    enabled: open && isCrmPersonProspect,
  });
  const isFetchingSignee = isCrmPersonProspect ? isCrmContactFetching : isCrmContactsLoading;
  const resolvedCrmContacts = isCrmPersonProspect ? (crmContact ? [crmContact] : []) : crmContacts;
  const { data: signeesData, isFetching: isSigneesFetching } = useSigneeOptionsQuery({
    accountId,
    inputValue,
    enabled: open,
  });
  const { data: usersData, isFetching: isUsersFetching } = useAccountUsersQuery();
  const isLoading = open && (isFetchingSignee || isSigneesFetching || isUsersFetching);

  const options = useMemo(() => {
    const { contacts, users } = buildSigneeOptions({
      crmContacts: resolvedCrmContacts,
      signeesData,
      usersData,
      inputValue,
    });
    return [...contacts, ...users];
  }, [resolvedCrmContacts, signeesData, usersData, inputValue]);

  const handleOpen = () => {
    setOpen(true);
    if (isCrmCompanyProspect) {
      refetchCrmContacts();
    } else if (isCrmPersonProspect) {
      refetchCrmContact();
    }
  };

  return (
    <Autocomplete
      autoHighlight
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      inputValue={inputValue}
      onInputChange={(event, value) => setInputValue(value)}
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      options={options}
      loading={isLoading}
      isOptionEqualToValue={(option, value) => option.value === value?.value}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.value}
      filterOptions={(x) => x}
      noOptionsText="No signatures found"
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Icon name="IcoSearch" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...restProps } = props;
        return (
          <li key={key} {...restProps}>
            <List.Item.Meta
              className="signature_dropdown__option__meta"
              avatar={<Avatar className={`user_color ${getSignatureColor(option.email)}`}>{option.initials}</Avatar>}
              title={option.label}
              description={option.email}
            />
          </li>
        );
      }}
    />
  );
}
